import { forwardRef } from "react";
import FirmProfile from "../../../config/firmProfile";

const styles = {
    page: {
        padding: '0',
        fontFamily: 'Times New Roman, serif',
        color: '#000',
        backgroundColor: '#fff',
        position: 'relative',
        minHeight: '100%',
    },
    headerContainer: {
        width: '100%',
        height: '40px',
        position: 'relative',
        marginBottom: '40px',
    },
    footerContainer: {
        width: '100%',
        height: '15px',
        marginTop: '20px',
        display: 'flex',
        alignItems: 'flex-end',
    },
    contentContainer: {
        padding: '0 40px',
    },
    logo: { height: '60px', marginBottom: '10px' },
    title: {
        textAlign: 'center',
        fontSize: '22px',
        fontWeight: 'bold',
        marginBottom: '4px',
        textTransform: 'uppercase',
    },
    subtitle: {
        textAlign: 'center',
        fontSize: '16px',
        fontWeight: 'bold',
        marginBottom: '4px',
    },
    re: {
        textAlign: 'center',
        fontSize: '13px',
        marginBottom: '20px',
        fontStyle: 'italic',
    },
    section: { marginBottom: '16px', pageBreakInside: 'avoid' },
    sectionTitle: {
        fontSize: '13px',
        fontWeight: 'bold',
        borderBottom: '1px solid #ccc',
        paddingBottom: '4px',
        marginBottom: '6px',
        textDecoration: 'underline',
        pageBreakAfter: 'avoid',
        breakAfter: 'avoid',
    },
    text: { fontSize: '11px', lineHeight: 1.5, marginBottom: '4px' },
    listItem: { fontSize: '11px', lineHeight: 1.5, marginBottom: '2px', paddingLeft: '16px' },
    table: { width: '100%', borderCollapse: 'collapse', marginBottom: '10px', pageBreakInside: 'avoid' },
    th: { borderBottom: '1px solid #000', textAlign: 'left', padding: '5px', fontSize: '11px', fontWeight: 'bold' },
    td: { borderBottom: '1px solid #eee', textAlign: 'left', padding: '5px', fontSize: '11px' },
    totalRow: { borderTop: '2px solid #000', fontWeight: 'bold' },
    signatureBlock: { marginTop: '30px', display: 'flex', justifyContent: 'space-between', pageBreakInside: 'avoid', breakInside: 'avoid' },
    signatureBox: { width: '45%' },
    sigText: { fontFamily: 'cursive', fontSize: '14px', color: '#1a237e', marginTop: '10px' },
    bottomText: { fontSize: '11px', lineHeight: 1.5, marginBottom: '8px', color: '#1a237e' },
    footerTextContainer: {
        width: '100%',
        textAlign: 'center',
        padding: '10px 20px 0',
        fontSize: '10px',
        color: '#666',
        backgroundColor: 'transparent',
    },
    headerLogoContainer: {
        display: 'flex',
        justifyContent: 'center',
        marginBottom: '20px',
    },
};

const BusinessEngagementTemplate = forwardRef(({ quotation }, ref) => {
    if (!quotation) return null;

    const {
        client,
        services,
        businessStructure,
        firmSignOff,
        acknowledgement,
        id,
        createdAt,
        engagementDetails,
    } = quotation;

    // Collect all service items
    const getAllServices = () => {
        const all = [];
        ['bookkeeping', 'taxation', 'structuring'].forEach(key => {
            if (services?.[key]?.selected) {
                services[key].selected.forEach(s => {
                    let label = s.label;
                    if (s.duration) label += ` (${s.duration})`;
                    all.push({ category: key, label, price: s.price, duration: s.duration });
                });
            }
            if (services?.[key]?.others) {
                services[key].others.forEach(s => {
                    if (s.text) {
                        let label = `Other: ${s.text}`;
                        if (s.duration) label += ` (${s.duration})`;
                        all.push({ category: key, label, price: s.price, duration: s.duration });
                    }
                });
            }
            if (services?.[key]?.other?.text) {
                all.push({ category: key, label: `Other: ${services[key].other.text}`, price: services[key].other.price, duration: services[key].other.duration });
            }
        });
        return all;
    };

    const serviceItems = getAllServices();
    const monthlyItems = serviceItems.filter(i => i.duration === 'Monthly');

    const getNoteForDuration = (dur) => {
        if (dur === 'Monthly') {
            return "Your monthly recurring cost will be the following and will automatically be deducted from your provided bank account.";
        } else if (dur === 'Yearly') {
            return "Your yearly payments will be the following and you will receive an invoice.";
        }
        else if (dur === 'Once') {
            return "Your one-time payments will be the following and you will receive an invoice.";
        }
        return `Your ${dur.toLowerCase()} payments will be the following and you will receive an invoice.`;
    };

    const groupedServices = serviceItems.reduce((acc, item) => {
        const dur = item.duration || 'Monthly';
        if (!acc[dur]) acc[dur] = [];
        acc[dur].push(item);
        return acc;
    }, {});

    const activeDurations = Object.keys(groupedServices).sort((a,b) => {
        const order = { 'Monthly': 1, 'Quarterly': 2, '6-Months': 3, 'Yearly': 4, 'Once': 5 };
        return (order[a] || 99) - (order[b] || 99);
    });

    // Build monthly fees sentence
    const buildMonthlyFeeSentence = () => {
        if (monthlyItems.length === 0) return null;
        const parts = monthlyItems.map(i => {
            const priceNum = Number(i.price || 0);
            const label = i.label.replace(/ \(Monthly\)/, '');
            if (priceNum === 0) {
                return { text: `${label} $0 per month. (Its included in your monthly package).`, isZero: true };
            }
            return { text: `${label} $${priceNum.toFixed(0)} per month`, isZero: false };
        });
        const mainItem = parts[0];
        const rest = parts.slice(1);
        return { mainItem, rest };
    };

    const monthlyFees = buildMonthlyFeeSentence();

    const startDate = engagementDetails?.startDate
        ? new Date(engagementDetails.startDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })
        : '__________';
    const endDate = engagementDetails?.endDate
        ? new Date(engagementDetails.endDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })
        : 'till further notice';

    const includeAccountingSoftwareFee = engagementDetails?.accountingSoftwareFee;
    const accountingSoftwareFeePrice = engagementDetails?.accountingSoftwareFeePrice ?? 30;

    const categoryLabel = (key) => key.charAt(0).toUpperCase() + key.slice(1);

    return (
        <div ref={ref} style={styles.page}>
            {/* Geometric Header */}
            <div style={styles.headerContainer}>
                <svg width="100%" height="40" viewBox="0 0 1000 40" preserveAspectRatio="none" style={{ display: 'block' }}>
                    <polygon points="0,0 150,0 175,20 0,20" fill="#7ec942" />
                    <polygon points="125,0 600,0 625,20 150,20" fill="#1a237e" />
                    <polygon points="625,20 655,20 675,40 645,40" fill="#7ec942" />
                    <polygon points="665,20 1000,20 1000,40 685,40" fill="#7ec942" />
                </svg>
            </div>

            <div style={styles.contentContainer}>
                {/* Logo */}
                <div style={styles.headerLogoContainer}>
                    {FirmProfile.LOGO_URL && <img src={FirmProfile.LOGO_URL} alt="Logo" style={styles.logo} />}
                </div>

                {/* Contact Info */}
                <div style={{ ...styles.text, textAlign: 'center', marginBottom: '16px', fontSize: '10px', color: '#666' }}>
                    <div>{FirmProfile.FOOTER_ABN_LINE}</div>
                    <div>Ph: {FirmProfile.CONTACT_DETAILS.phone} | Email: {FirmProfile.CONTACT_DETAILS.email} | {FirmProfile.CONTACT_DETAILS.website}</div>
                    <div>{FirmProfile.CONTACT_DETAILS.address}</div>
                </div>

                {/* Title */}
                <div style={styles.title}>ASPIRE TAX AND BOOKKEEPING</div>
                <div style={styles.subtitle}>Business Engagement Form</div>
                <div style={styles.re}>Re: Engagement of our services</div>

                {/* Meta */}
                <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between' }}>
                    <span style={styles.text}><strong>Reference:</strong> {id}</span>
                    <span style={styles.text}><strong>Date:</strong> {new Date(createdAt || Date.now()).toLocaleDateString()}</span>
                </div>

                {/* Dear */}
                <div style={styles.section}>
                    <p style={styles.text}>Dear {client?.clientName || '[Name]'},</p>
                    <p style={styles.text}>
                        Further to our recent discussions, we are pleased to accept the appointment as your accountant and tax agent and now set out our understanding of the scope and terms of this engagement.
                    </p>
                    <p style={styles.text}>We will be responsible for the following:</p>
                </div>

                {/* Services Tables Grouped by Duration */}
                <div style={styles.section}>
                    {activeDurations.map(dur => {
                        const items = groupedServices[dur];
                        const subtotal = items.reduce((sum, item) => sum + Number(item.price || 0), 0);
                        const gst = subtotal * 0.1;
                        const grandTotal = subtotal + gst;
                        const note = getNoteForDuration(dur);

                        return (
                            <div key={dur} style={{ marginBottom: '25px' }}>
                                <div style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '8px' }}>
                                    {dur} Services
                                </div>
                                <p style={{...styles.text, fontStyle: 'italic', marginBottom: '8px', color: '#444'}}>
                                    {note}
                                </p>
                                <table style={styles.table}>
                                    <thead>
                                        <tr>
                                            <th style={styles.th}>Service Category</th>
                                            <th style={styles.th}>Description</th>
                                            <th style={{ ...styles.th, textAlign: 'right' }}>Amount (AUD)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {items.map((item, idx) => (
                                            <tr key={`${item.category}-${idx}`}>
                                                <td style={styles.td}>{categoryLabel(item.category)}</td>
                                                <td style={styles.td}>{item.label}</td>
                                                <td style={{ ...styles.td, textAlign: 'right' }}>${Number(item.price || 0).toFixed(2)}</td>
                                            </tr>
                                        ))}
                                        <tr style={styles.totalRow}>
                                            <td colSpan={2} style={{ ...styles.td, textAlign: 'right' }}>Subtotal (Excl. GST)</td>
                                            <td style={{ ...styles.td, textAlign: 'right' }}>${subtotal.toFixed(2)}</td>
                                        </tr>
                                        <tr>
                                            <td colSpan={2} style={{ ...styles.td, textAlign: 'right' }}>GST (10%)</td>
                                            <td style={{ ...styles.td, textAlign: 'right' }}>${gst.toFixed(2)}</td>
                                        </tr>
                                        <tr style={{ ...styles.totalRow, backgroundColor: '#f9f9f9' }}>
                                            <td colSpan={2} style={{ ...styles.td, textAlign: 'right', fontWeight: 'bold' }}>Total (Incl. GST)</td>
                                            <td style={{ ...styles.td, textAlign: 'right', fontWeight: 'bold' }}>${grandTotal.toFixed(2)}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        );
                    })}
                </div>

                <div style={styles.section}>
                    <p style={styles.text}>
                        If we are requested to provide other specific service(s) they will be the subject of a separate engagement letter and thus a separate fee.
                    </p>
                    <p style={styles.text}>
                        This work will be conducted in accordance with the relevant professional and ethical standards issued by the Accounting Professional & Ethical Standards Board Limited (APESB).
                    </p>
                    <p style={styles.text}>
                        We understand that an audit of the Financial Accounts is not required, and therefore we will not be expressing an opinion as to the truth and fairness of those statements. However, we will inform you of any such matters that come to our attention.
                    </p>
                </div>

                {/* Information Required */}
                <div style={styles.section}>
                    <div style={styles.sectionTitle}>Information Required From You</div>
                    <p style={styles.text}>
                        To help us prepare your Financial Accounts and Income Tax Returns, you will need to provide us the following:
                    </p>
                    {[
                        'Up-to-date and reconciled General Ledger/Cash Book.',
                        'Copies of your Business Activity Statements that were reported to ATO.',
                        'Any loan documentations.',
                        'Copy of Bank Statements.',
                        'Details of new assets purchased (including cars for FBT purposes).',
                        'Completed FBT Questionnaire.',
                        'PAYGW summaries.',
                        'Listing of superannuation payments by the employee.',
                        'Other items as requested from time to time.',
                    ].map((item, i) => (
                        <p key={i} style={styles.listItem}>• {item}</p>
                    ))}
                </div>

                {/* Responsibilities */}
                <div style={styles.section}>
                    <div style={styles.sectionTitle}>Responsibilities</div>
                    <p style={styles.text}>
                        You or your staff will be responsible for maintaining and regularly balancing all books of accounts, and the maintenance of an adequate accounting and internal control system. You also acknowledge that the reliability, accuracy and completeness of the accounting records are your responsibility and that you have disclosed to us all material and relevant information.
                    </p>
                    <p style={styles.text}>
                        Our involvement in this type of engagement will not disclose fraud, defalcations or other irregularities which may occur. However, any material weaknesses in the accounting or internal control systems which come to our notice will be drawn to your attention.
                    </p>
                    <p style={styles.text}>Please be aware that:</p>
                    <p style={styles.listItem}>(a) You are responsible for the accuracy and completeness of the particulars and information provided in relation to taxation services and this responsibility rests with you;</p>
                    <p style={styles.listItem}>(b) Any advice given to you is only an opinion based on our knowledge of your particular circumstances; and</p>
                    <p style={styles.listItem}>(c) A taxpayer has obligations under self assessment to keep full and proper records in order to facilitate the preparation of accurate returns. It is the taxpayer's responsibility to keep those records for five (5) years.</p>
                    <p style={styles.text}>
                        Any information acquired by us in the course of our engagement is subject to strict confidentiality requirements. Information will not be disclosed by us to other parties except as required or allowed for by law or professional standards, or with your express consent. Our files may, however, be subject to review as part of the quality control review program of CPA Australia which monitors compliance with professional standards by its members. We advise you that by signing this letter you acknowledge that, if requested, our files relating to this engagement will be made available under this program. Should this occur, we will advise you. The same strict confidentiality requirements apply under this program as apply to us.
                    </p>
                    <p style={styles.text}>
                        We may collect Personal Information about your representatives, your clients and others when we provide services to you. If we do, you agree to work with us to ensure that we both meet the obligations that we each may have under the Privacy Act 1988 (Cth) (as amended) (Privacy Act). The obligations may include notifying the relevant person to whom the personal information relates who we are and how we propose to use their personal information. Where you have collected personal information, you confirm that you have collected the personal information in accordance with the Privacy Act, that you are entitled to provide this personal information to us and that we may use and disclose the personal information for the purpose/s we provide our services to you. We will handle personal information in accordance with the Privacy Act.
                    </p>
                </div>

                {/* Self Assessment */}
                <div style={styles.section}>
                    <div style={styles.sectionTitle}>Self Assessment & Review</div>
                    <p style={styles.text}>
                        A taxpayer is responsible under self assessment to keep full and proper records in order to facilitate the preparation of a correct return. Whilst the Commissioner of Taxation will accept claims made by a taxpayer in an income tax return and issue a notice of assessment, usually without adjustment, the return may be subject to later review. Under the taxation law such a review may take place within a period of up to four (4) years after tax becomes due and payable under the assessment. Furthermore, where there is fraud or evasion there is no time limit on amending the assessment. Accordingly, you should check the return before it is signed to ensure that the information in the return is accurate.
                    </p>
                    <p style={styles.text}>
                        Where the application of a taxation law to your particular circumstances is uncertain you also have the right to request a private ruling which will set out the Commissioner's opinion about the way a taxation law applies, or would apply, to you in those circumstances. You must provide a description of all of the facts (with supporting documentation) that are relevant to your scheme or circumstances in your private ruling application. If there is any material difference between the facts set out in the ruling and what you actually do the private ruling is ineffective.
                    </p>
                    <p style={styles.text}>
                        If you rely on a private ruling you have received, the Commissioner must administer the law in the way set out in the ruling, unless it is found to be incorrect and applying the law correctly would lead to a better outcome for you. Where you disagree with the decision in the private ruling, or the Commissioner fails to issue such a ruling, you can lodge an objection against the ruling if it relates to income tax, fuel tax credit or fringe benefits tax. Your time limits in lodging an objection will depend on whether you are issued an assessment for the matter (or period) covered by the private ruling.
                    </p>
                </div>

                {/* Involvement of Others */}
                <div style={styles.section}>
                    <div style={styles.sectionTitle}>Involvement of Others</div>
                    <p style={styles.text}>
                        Where, as part of our engagement, the services of an external consultant or expert are required, an estimated cost and timeframe and involvement will be provided to you for your approval.
                    </p>
                </div>

                {/* Ownership of Documents */}
                <div style={styles.section}>
                    <div style={styles.sectionTitle}>Ownership of Documents</div>
                    <p style={styles.text}>
                        The final Financial Statements, Income Tax Returns and any other documents which we are specifically engaged to prepare, together with any original documents given to us by you, shall be the property of you. Any other documents brought into existence by us including general journals, working papers, the general ledger, draft financial statements and copies of tax returns, will remain our property at all times.
                    </p>
                </div>

                {/* Lien on Documents */}
                <div style={styles.section}>
                    <div style={styles.sectionTitle}>Lien on Documents</div>
                    <p style={styles.text}>
                        In relation to any subsequent termination of our services, you are advised that we shall be entitled to retain all documents belonging to you and any related parties we act for until payment is received in full for all outstanding fees.
                    </p>
                </div>

                {/* Fees */}
                <div style={styles.section}>
                    <div style={styles.sectionTitle}>Fees</div>
                    <p style={styles.text}>
                        Any additional fee for an additional service not covered in the scope of this engagement will be the subject of a separate engagement letter under separate fee arrangements.
                    </p>
                    {monthlyFees && (
                        <>
                            <p style={styles.text}>
                                Accounting monthly Fees: {monthlyFees.mainItem.text}
                                {monthlyFees.rest.length > 0 
                                    ? (monthlyFees.mainItem.isZero ? ' Given below are some of the other charges charged monthly.' : ', given below are some of the other charges charged monthly.')
                                    : (monthlyFees.mainItem.isZero ? '' : '.')}
                            </p>
                            {monthlyFees.rest.length > 0 && (
                                monthlyFees.rest.map((item, i) => (
                                    <p key={i} style={styles.listItem}>• {item.text}{item.isZero ? '' : ' payable monthly.'}</p>
                                ))
                            )}
                        </>
                    )}
                    {includeAccountingSoftwareFee && (
                        <p style={styles.listItem}>
                            • Accounting Software fees ${Number(accountingSoftwareFeePrice || 0).toFixed(0)} per month payable monthly.
                        </p>
                    )}
                    <p style={styles.text}>
                        It is our requirement that the owners/directors of the business personally guarantee the payment of all accounts for professional fees, including out-of-pocket expenses rendered by us from time to time.
                    </p>
                </div>

                {/* Period of Engagement */}
                <div style={styles.section}>
                    <div style={styles.sectionTitle}>Period of Engagement</div>
                    <p style={styles.text}>
                        This engagement will start on {startDate}. The first period for which we will be responsible is tax year ending 2026.
                    </p>
                    <p style={styles.text}>
                        This engagement will remain in effect {endDate === 'till further notice' ? 'till further notice' : `until ${endDate}`}.
                    </p>
                    <p style={styles.text}>
                        This engagement document will be effective for future years unless we issue an amended one to you.
                    </p>
                </div>

                {/* Limitation of Liability */}
                <div style={styles.section}>
                    <div style={styles.sectionTitle}>Limitation of Liability</div>
                    <p style={styles.text}>
                        Our liability is limited by a scheme approved under Professional Standards Legislation. Further information on the scheme is available from the Professional Standards Councils' website:
                    </p>
                    <p style={{ ...styles.text, color: '#1a237e' }}>http://www.professionalstandardscouncil.gov.au</p>
                </div>

                {(businessStructure?.hasClosingStatement === true || businessStructure?.hasClosingStatement === 'true' || businessStructure?.hasClosingStatement === 'yes') && businessStructure?.closingStatementText && (
                    <div style={styles.section}>
                        {businessStructure.closingStatementText.split('\n').map((line, idx) => (
                            <p key={idx} style={{ ...styles.text, fontStyle: 'italic', margin: '0 0 4px 0', minHeight: '1.2em' }}>{line}</p>
                        ))}
                    </div>
                )}

                <p style={styles.text}>
                    Please sign and return the attached copy of this letter to indicate that it is in accordance with your understanding of the arrangements.
                </p>

                {/* Signatures */}
                <div style={styles.signatureBlock}>
                    {/* Firm */}
                    <div style={styles.signatureBox}>
                        <div style={styles.sectionTitle}>Firm Sign-off</div>
                        <div style={styles.text}>Kind Regards,</div>
                        <div style={styles.sigText}>
                            Digitally signed by {FirmProfile.PRINCIPAL_SIGNER.name} on {firmSignOff?.signedDate}
                        </div>
                        <div style={styles.bottomText}>
                            <strong>{FirmProfile.PRINCIPAL_SIGNER.name}</strong><br />
                            {FirmProfile.PRINCIPAL_SIGNER.title}<br />
                            {FirmProfile.COMPANY_NAME}
                        </div>
                    </div>

                    {/* Client */}
                    <div style={styles.signatureBox}>
                        <div style={styles.sectionTitle}>Client Acknowledgement</div>
                        {acknowledgement?.directorName && (
                            <>
                                <div style={styles.text}>
                                    On Behalf Of: {acknowledgement.onBehalfOf}
                                </div>
                                <div style={styles.sigText}>
                                    Digitally signed by {acknowledgement.directorName} on {acknowledgement.clientSignedDate}
                                </div>
                                <div style={styles.text}>
                                    <strong>{acknowledgement.directorName}</strong>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* Geometric Footer */}
                <div style={{ marginTop: '20px' }}>
                    <svg width="100%" height="15" viewBox="0 0 1000 15" preserveAspectRatio="none" style={{ display: 'block' }}>
                        <polygon points="0,0 300,0 330,15 0,15" fill="#7ec942" />
                        <polygon points="330,0 380,0 430,15 380,15" fill="#7ec942" />
                        <polygon points="430,0 730,0 760,15 460,15" fill="#1a237e" />
                        <polygon points="760,0 810,0 860,15 810,15" fill="#7ec942" />
                        <polygon points="860,0 1000,0 1000,15 910,15" fill="#7ec942" />
                    </svg>
                </div>
            </div>
        </div>
    );
});

BusinessEngagementTemplate.displayName = "BusinessEngagementTemplate";

export default BusinessEngagementTemplate;
